import { useWebSocket, useDocumentVisibility } from '@vueuse/core';

export default defineNuxtPlugin((nuxtApp) => {
  const websocketClosedNotify = ref(null);
  const isConnecting = ref(false);

  // Chat messages queued while the socket is not OPEN, flushed on reconnect.
  // Prevents silent message loss during connection drops ([ws] socket is not open to sendObj).
  const pendingMessages = [];
  const MAX_PENDING_MESSAGES = 100;
  const MAX_PENDING_AGE = ms('2m'); // messages older than this are stale — replaying them would confuse the recipient
  const RECONNECT_RETRY_AFTER_FAILED = ms('30s'); // cadence once the fast retry budget is spent

  const commandsDispatchMap = {
    messages: 'chat/onFetchMessages',
    new_message: 'chat/onNewMessage',
    edit_message: 'chat/onEditMessage',
    update_user: 'websocket/updateUser',
    update_operator: 'websocket/updateOperator',
    new_request: 'websocket/addRequest',
    update_request: 'websocket/updateRequest',
    delete_request: 'websocket/removeRequest',
    'forward-request': 'websocket/forwardRequest',
    'forward-request-declined': 'websocket/forwardedRequest',
    invite_assist_request: 'websocket/inviteAssistRequest',
    decline_assist_request: 'websocket/declineAssistRequest',
    request_queue: 'websocket/requestQueue',
    read_private_message: 'chat/updateMessageReadBy',
    read_room_message: 'chat/updateMessageReadBy',
    read_request_message: 'chat/updateMessageReadBy',
    refresh_browser: null,
    logout_everywhere: null,
  };

  const unhandledCommands = [
    'request_equip_update',
    'join_audio_transcript',
    'declined_audio_transcript',
    'accepted_audio_transcript',
    'declined_audio_session',
  ];

  // Computed
  const websocketUrl = computed(() => {
    let token = window.localStorage.getItem('token');

    if (useAuthStore().isLoggedIn && token?.length) {
      const accessToken = token.split(' ')[1]; // Return accessToken without Bearer prefix
      return `${useApi.getBaseURL().replace('http', 'ws')}/socket?token=${accessToken}`;
    } else {
      return;
    }
  });

  // Methods
  const showErrorNotify = () => {
    if (!websocketClosedNotify.value && useAuthStore().isLoggedIn) {
      if (!document.title.includes('🔴')) {
        document.title = `🔴 ${document.title}`;
      }

      websocketClosedNotify.value = ElNotification.error({
        title: 'Došlo k chybě při spojení',
        message: '<a href="#" onclick="window.location.reload()">Zkusit znovu</a>',
        dangerouslyUseHTMLString: true,
        duration: 0,
        offset: 80,
        position: 'top-right',
        showClose: false,
        customClass: 'el-notification--danger',
        onClose: () => {
          if (document.title.includes('🔴')) {
            document.title = document.title.replace('🔴 ', '');
          }
        },
      });
    }
  };

  const onConnected = () => {
    console.log('[ws] connected');
    isConnecting.value = false;
    nuxtApp.$store.commit('websocket/SOCKET_ONOPEN');

    if (websocketClosedNotify.value) {
      websocketClosedNotify.value.close();
      websocketClosedNotify.value = null;
    }

    flushPendingMessages();
  };

  const onDisconnected = (ws, event) => {
    console.log('[ws] disconnected', event?.code, event);
    isConnecting.value = false;
    nuxtApp.$store.commit('websocket/SOCKET_ONCLOSE');

    if (event.code === 1000 || event.code === 1001 || event.code === 1005) {
      console.log('[ws] disconnected silently');
    } else {
      showErrorNotify();
    }
  };

  const onMessage = (ws, event) => {
    let payload = false;
    try {
      payload = JSON.parse(event.data);
    } catch (error) {
      console.error('[ws] UnexpectedJSONParseError: ', error.message, event.data);
    }

    if (payload) {
      if (payload.command === 'pong') {
        // Heartbeat reply from the backend. useWebSocket already reset its pong timer on this
        // frame (any inbound message does), so just swallow it here to keep the console clean.
        return;
      }

      console.log('[ws] message ', JSON.parse(JSON.stringify(payload)));
      nuxtApp.$store.commit('websocket/SOCKET_ONMESSAGE', _cloneDeep(payload));

      if (payload.command && commandsDispatchMap[payload.command]) {
        nuxtApp.$store.dispatch(commandsDispatchMap[payload.command], payload);
      } else if (payload.command && payload.command === 'logout_everywhere') {
        if (useAuthStore().isLoggedIn) {
          useLogout();
        }
      } else if (payload.command && payload.command === 'refresh_browser') {
        // The operator's "Obnovit aplikaci klienta". Reload without asking: the client is
        // typically deaf and mid-call, so a confirmation would only get in the way.
        window.location.reload();
      } else if (payload.command && unhandledCommands.includes(payload.command)) {
        // own handling in components watch
      } else {
        console.warn('[ws] Unknown command: ', payload);
      }
    }
  };

  const onError = (ws, event) => {
    console.log('[ws] error', event);
    nuxtApp.$store.commit('websocket/SOCKET_ONCLOSE');
    showErrorNotify();
  };


  const socket = useWebSocket(() => websocketUrl.value || undefined, {
    immediate: false,
    autoReconnect: {
      retries: 50,
      delay: ms('2s'),
      onFailed: () => {
        // The 50 retries above are spent after ~100s and useWebSocket then stops for good;
        // the only thing that reopens the socket is the visibility watcher below, so a tab
        // left in the foreground never recovers and the offline sound loops on (#1562).
        console.log('[ws] autoReconnect retries failed, retrying in', RECONNECT_RETRY_AFTER_FAILED);
        setTimeout(safeOpen, RECONNECT_RETRY_AFTER_FAILED);
      },
    },
    // App-level heartbeat: lets the browser detect half-open (dead) connections —
    // when no message arrives within pongTimeout of a ping, the socket is closed
    // and autoReconnect takes over. The pong itself is swallowed in onMessage.
    heartbeat: {
      message: JSON.stringify({ command: 'ping' }),
      interval: ms('60s'),
      pongTimeout: ms('15s'),
    },
    onConnected,
    onDisconnected,
    onError,
    onMessage,
  });

  const queueMessage = (payload) => {
    // Typing indicators are ephemeral — replaying stale ones after reconnect is wrong.
    if (payload?.is_typing) {
      console.warn('[ws] socket not open, dropping typing indicator', socket?.status?.value);
      return;
    }

    // Only chat messages (identified by temp_id, which the server dedups on its echo) are safe
    // to replay after reconnect. One-shot commands (forward/equip/precall/audio, read receipts)
    // would fire against changed state minutes later, so drop them as before the queue existed.
    if (!payload?.temp_id) {
      console.warn('[ws] socket not open, dropping command', payload?.command, socket?.status?.value);
      return;
    }

    if (pendingMessages.length >= MAX_PENDING_MESSAGES) {
      pendingMessages.shift();
    }
    pendingMessages.push({ payload, queuedAt: Date.now() });
    console.warn('[ws] socket not open, queued message for reconnect', socket?.status?.value, `(queue: ${pendingMessages.length})`);
  };

  const flushPendingMessages = () => {
    if (!pendingMessages.length) {
      return;
    }
    // Empty the queue before re-sending so a failed send can safely re-queue.
    const queued = pendingMessages.splice(0);
    const fresh = queued.filter(({ queuedAt }) => Date.now() - queuedAt <= MAX_PENDING_AGE);
    if (fresh.length < queued.length) {
      console.warn('[ws] dropped', queued.length - fresh.length, 'stale queued message(s)');
    }
    console.log('[ws] flushing', fresh.length, 'queued message(s)');
    fresh.forEach(({ payload }) => socket.sendObj(payload));
  };

  socket.sendObj = (payload) => {
    // socket.status only updates on open/close events and can lag behind reality —
    // the browser silently drops sends into a CLOSING socket, so check readyState directly.
    if (socket.ws?.value?.readyState === WebSocket.OPEN) {
      try {
        console.log('[ws] send', payload);
        socket.ws.value.send(JSON.stringify(payload));
      } catch (error) {
        console.warn('[ws] send failed, queueing message', error.name);
        queueMessage(payload);
      }
    } else {
      queueMessage(payload);
    }
  };

  // Once a chat message is marked as failed in the UI (manual resend takes over),
  // it must leave the queue — otherwise the reconnect flush would deliver it twice.
  socket.discardQueuedMessage = (temp_id) => {
    const index = pendingMessages.findIndex(({ payload }) => payload.temp_id === temp_id);
    if (index !== -1) {
      pendingMessages.splice(index, 1);
      console.log('[ws] discarded queued message', temp_id);
    }
  };

  // Safe open function to prevent multiple simultaneous connections
  const safeOpen = () => {
    if (socket.status.value === 'OPEN' || socket.status.value === 'CONNECTING') {
      console.log('[ws] Connection already', socket.status.value, '- skipping open');
      return;
    }

    if (socket.status.value === 'CLOSED' && !isConnecting.value && websocketUrl.value) {
      console.log('[ws] Opening connection...');
      isConnecting.value = true;
      socket.open();
    }
  };

  // Helpers for debugging
  window.websocketClose = () => socket.close();
  window.websocketOpen = () => safeOpen();
  window.websocketSendObj = (payload) => socket.sendObj(payload);

  // Watchers
  watch(useDocumentVisibility(), (visibility) => {
    if (visibility === 'visible') {
      safeOpen();
    }
  });

  watch(websocketUrl, () => { // init socket on page load
    if (!websocketUrl.value) {
      // Logged out — queued commands must not leak into the next session.
      pendingMessages.length = 0;
    }
    safeOpen();
  }, { immediate: true });

  return {
    provide: {
      socket: socket,
    },
  };
});
